// Study Helper's toolbar button and side panel. The panel is an iframe of /tools/helper?panel=1,
// so none of the Helper's code is in the app bundle. Docked on the right on laptops (resizable,
// 320 to 520 px), a bottom sheet under 720 px. Shown only when the server flag lets this browser
// have it and the Study Helper workspace extension is on.
import { get, subscribe } from "../shared/settings-store.js";
import { openInternal } from "./tabs.js";
import { frames } from "./view.js";
import { state } from "./state.js";
import { handoffSenderOk, cleanHandoff } from "../shared/helper-handoff.js";

const FLAG_KEY = "revault:helper:flag";
const W_KEY = "revault:helper:w";
const NARROW = "(max-width: 719px)";
const ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l1.8 3.9 4.2.6-3 2.9.7 4.2L12 12.6 8.3 14.6l.7-4.2-3-2.9 4.2-.6z"></path><path d="M6 20h12"></path></svg>';
const CSS = ".rv-helper-dock{position:fixed;z-index:60;background:var(--bg);border-left:1px solid var(--line);display:flex}.rv-helper-dock iframe{border:0;flex:1;width:100%;height:100%;background:var(--bg)}.rv-helper-dock[data-mode=side]{top:0;right:0;bottom:0;width:var(--rv-helper-w,380px)}html[data-helper=side] .rv-root{width:calc(100vw - var(--rv-helper-w,380px))}html[data-helper=side] .rv-home{display:none}.rv-helper-grip{flex:none;width:10px;cursor:ew-resize;touch-action:none;background:transparent;border:0;padding:0;position:relative}.rv-helper-grip::after{content:'';position:absolute;left:4px;top:50%;width:2px;height:40px;margin-top:-20px;background:var(--line-strong);border-radius:2px}.rv-helper-grip:focus-visible{outline:2px solid var(--accent-ink);outline-offset:-2px}.rv-helper-dock[data-mode=sheet]{left:0;right:0;bottom:0;height:min(78vh,640px);border:1px solid var(--line);border-radius:var(--radius-lg) var(--radius-lg) 0 0;flex-direction:column}.rv-helper-dock[data-mode=sheet] .rv-helper-grip{display:none}.rv-helper-veil{position:fixed;inset:0;z-index:59;background:rgba(0,0,0,.45)}";

let btn = null, dock = null, frame = null, grip = null, veil = null;
let ready = false, pending = null, opener = null, isOpen = false;

const flag = () => { try { return localStorage.getItem(FLAG_KEY) || "off"; } catch (e) { return "off"; } };
const signedIn = () => { try { return !!JSON.parse(localStorage.getItem("revault:account") || "null"); } catch (e) { return false; } };
function enabled() {
	const f = flag();
	if (f === "off" || get("extHelper") === false) return false;
	return f === "on" || signedIn();
}

export function initHelperDock() {
	window.addEventListener("rv:helper", (e) => { try { localStorage.setItem(FLAG_KEY, String(e.detail || "off")); } catch (err) { /* private mode */ } sync(); });
	subscribe(sync);
	window.addEventListener("message", onMessage);
	sync();
}

function sync() {
	const on = enabled();
	if (on && !btn) {
		const more = document.getElementById("rv-nav-more");
		const ext = document.getElementById("rv-ext");
		if (!more) return;
		btn = document.createElement("button");
		btn.className = "rv-nav rv-helper-btn"; btn.id = "rv-helper-btn"; btn.type = "button";
		btn.title = "Study Helper (Alt+H)"; btn.setAttribute("aria-label", "Study Helper"); btn.setAttribute("aria-expanded", "false"); btn.setAttribute("aria-controls", "rv-helper-dock");
		btn.innerHTML = ICON;
		btn.addEventListener("click", () => toggleHelper());
		more.insertBefore(btn, ext || null);
	}
	if (btn) btn.hidden = !on;
	if (!on && isOpen) close(false);
}

// Alt+H from the app or from a page in a tab. Returns true when it handled the key.
export function helperKey(e) {
	if (!e.altKey || e.ctrlKey || e.metaKey || e.code !== "KeyH") return false;
	if (!enabled()) return false;
	try { const p = JSON.parse(localStorage.getItem("revault:helper") || "{}"); if (p.altH === false) return false; } catch (err) { /* defaults */ }
	e.preventDefault();
	toggleHelper();
	return true;
}

export function toggleHelper() { if (isOpen) close(true); else open(); }

function width() { const w = +(localStorage.getItem(W_KEY) || 380); return Math.min(520, Math.max(320, w || 380)); }
function setWidth(w) {
	w = Math.min(520, Math.max(320, Math.round(w)));
	document.documentElement.style.setProperty("--rv-helper-w", w + "px");
	if (grip) grip.setAttribute("aria-valuenow", String(w));
	try { localStorage.setItem(W_KEY, String(w)); } catch (e) { /* private mode */ }
}

function build() {
	const st = document.createElement("style"); st.textContent = CSS; document.head.append(st);
	dock = document.createElement("div");
	dock.id = "rv-helper-dock"; dock.className = "rv-helper-dock";
	grip = document.createElement("div");
	grip.className = "rv-helper-grip"; grip.tabIndex = 0;
	grip.setAttribute("role", "separator"); grip.setAttribute("aria-orientation", "vertical"); grip.setAttribute("aria-label", "Resize Study Helper. Arrow keys change the width.");
	grip.setAttribute("aria-valuemin", "320"); grip.setAttribute("aria-valuemax", "520");
	grip.addEventListener("keydown", (e) => {
		if (e.key === "ArrowLeft") { e.preventDefault(); setWidth(width() + 20); }
		else if (e.key === "ArrowRight") { e.preventDefault(); setWidth(width() - 20); }
		else if (e.key === "Home") { e.preventDefault(); setWidth(520); }
		else if (e.key === "End") { e.preventDefault(); setWidth(320); }
	});
	let drag = null, moved = false;
	grip.addEventListener("pointerdown", (e) => { drag = { x: e.clientX, w: width() }; moved = false; grip.setPointerCapture(e.pointerId); frame.style.pointerEvents = "none"; });
	grip.addEventListener("pointermove", (e) => { if (!drag) return; if (Math.abs(e.clientX - drag.x) > 3) moved = true; setWidth(drag.w + (drag.x - e.clientX)); });
	const end = () => { if (!drag) return; drag = null; frame.style.pointerEvents = ""; if (!moved) setWidth(width() >= 450 ? 380 : 520); };
	grip.addEventListener("pointerup", end); grip.addEventListener("pointercancel", end);
	frame = document.createElement("iframe");
	frame.title = "Study Helper";
	frame.src = "/tools/helper?panel=1";
	frame.addEventListener("load", () => { ready = false; });
	dock.append(grip, frame);
	document.body.append(dock);
	matchMedia(NARROW).addEventListener("change", () => { if (isOpen) layout(); });
	// A modal bottom sheet keeps focus inside: anything that lands outside goes back in.
	document.addEventListener("focusin", (e) => { if (isOpen && dock.dataset.mode === "sheet" && !dock.contains(e.target)) frame.focus(); });
}

function layout() {
	const sheet = matchMedia(NARROW).matches;
	dock.dataset.mode = sheet ? "sheet" : "side";
	if (sheet) { dock.setAttribute("role", "dialog"); dock.setAttribute("aria-modal", "true"); dock.setAttribute("aria-label", "Study Helper"); document.documentElement.dataset.helper = "sheet"; }
	else { dock.setAttribute("role", "complementary"); dock.removeAttribute("aria-modal"); dock.setAttribute("aria-label", "Study Helper"); document.documentElement.dataset.helper = "side"; }
	if (sheet && !veil) { veil = document.createElement("div"); veil.className = "rv-helper-veil"; veil.addEventListener("click", () => close(true)); document.body.append(veil); }
	if (veil) veil.hidden = !sheet;
	setWidth(width());
}

function open() {
	opener = document.activeElement;
	if (!dock) build();
	dock.hidden = false;
	isOpen = true;
	layout();
	if (btn) btn.setAttribute("aria-expanded", "true");
	post({ type: "revault-helper-panel", open: true });
	setTimeout(() => { try { frame.focus(); frame.contentWindow.document.querySelector(".hp-input") && frame.contentWindow.document.querySelector(".hp-input").focus(); } catch (e) { /* not loaded yet */ } }, ready ? 0 : 400);
}

function close(refocus) {
	if (!dock) return;
	dock.hidden = true;
	isOpen = false;
	if (veil) veil.hidden = true;
	delete document.documentElement.dataset.helper;
	if (btn) btn.setAttribute("aria-expanded", "false");
	post({ type: "revault-helper-panel", open: false });
	if (refocus) { const back = opener && opener.isConnected ? opener : btn; if (back) back.focus(); }
}

function post(m) { try { if (frame && frame.contentWindow) frame.contentWindow.postMessage(m, location.origin); } catch (e) { /* not ready */ } }

function onMessage(e) {
	if (e.origin !== location.origin || !e.data) return;
	const d = e.data;
	if (d.type === "revault-helper-ready" && frame && e.source === frame.contentWindow) {
		ready = true;
		if (pending) { post(pending); pending = null; }
		// Opened before the Helper had loaded: now put focus in its composer.
		if (isOpen && dock.contains(document.activeElement)) { frame.focus(); post({ type: "revault-helper-panel", open: true }); }
		return;
	}
	if (d.type === "revault-helper-close" && frame && e.source === frame.contentWindow) { close(true); return; }
	if (d.type === "revault-helper-tool" && frame && e.source === frame.contentWindow && /^(?:flashcards|timer|notes)$/.test(d.id)) { openInternal("/tools/" + d.id, d.id === "flashcards" ? "Flashcards" : d.id === "timer" ? "Timer" : "Notes"); return; }
	// A Notes tab asking for help with a note. Only the Notes tab itself may ask (matched by its
	// frame's window, so a page in another tab or a book cannot), and the Helper still asks the
	// student before the text is attached.
	if (d.type === "revault-helper-open" && enabled() && handoffSenderOk(e.source, frames, state.tabs)) {
		const msg = cleanHandoff(d);
		if (!isOpen) open();
		if (ready) post(msg); else pending = msg;
	}
}
